import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useAgent } from "@/client/hooks/use-agent";
import { buildContextDocument } from "@/client/lib/agent-context/build-context-doc";

export function WhatAiKnowsPage(): React.ReactElement {
  const { contextEntries, quietMode, setQuietMode } = useAgent();
  const [howToDisableOpen, setHowToDisableOpen] = useState(false);

  const contextDoc = buildContextDocument();

  return (
    <div className="space-y-6 max-w-3xl" data-testid="what-ai-knows-page">
      <div>
        <h2 className="text-lg font-semibold">What the AI knows</h2>
        <p className="text-sm text-muted-foreground mt-1">
          This page shows the live context sent to the AI assistant with every message. Nothing is stored between
          sessions unless you explicitly ask the assistant to remember something.
        </p>
      </div>

      {/* Context entries */}
      <section aria-labelledby="context-entries-heading">
        <h3 className="text-sm font-semibold mb-2" id="context-entries-heading">
          Live context entries ({contextEntries.length})
        </h3>
        {contextEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No entries registered yet.</p>
        ) : (
          <div className="space-y-3">
            {[...contextEntries]
              .sort((a, b) => b.priority - a.priority)
              .map((entry) => (
                <div
                  key={entry.key}
                  className="rounded-md border bg-card p-3 space-y-1"
                  data-testid={`context-entry-${entry.key}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{entry.label}</span>
                    <span className="text-xs text-muted-foreground">priority {entry.priority}</span>
                  </div>
                  <pre className="text-xs bg-muted rounded p-2 overflow-auto whitespace-pre-wrap break-all">
                    {typeof entry.value === "string" ? entry.value : JSON.stringify(entry.value, null, 2)}
                  </pre>
                </div>
              ))}
          </div>
        )}
      </section>

      {/* Rendered context doc */}
      <section aria-labelledby="context-doc-heading">
        <h3 className="text-sm font-semibold mb-2" id="context-doc-heading">
          Context document sent to the model
        </h3>
        <p className="text-xs text-muted-foreground mb-2">
          This is the exact markdown block appended to the system prompt for each message.
        </p>
        <pre
          className="text-xs bg-muted rounded-md p-3 overflow-auto whitespace-pre-wrap border"
          data-testid="context-doc-preview"
        >
          {contextDoc || "(empty — no context registered)"}
        </pre>
      </section>

      {/* Stored preferences */}
      <section aria-labelledby="stored-prefs-heading">
        <h3 className="text-sm font-semibold mb-2" id="stored-prefs-heading">
          Stored preferences
        </h3>
        <p className="text-sm text-muted-foreground">
          (none stored) — persistent per-user AI preferences are planned for phase 2.
        </p>
      </section>

      {/* How to disable expander */}
      <section aria-labelledby="how-to-disable-heading">
        <button
          type="button"
          className="flex items-center gap-2 text-sm font-semibold hover:text-primary transition-colors"
          onClick={() => setHowToDisableOpen((v) => !v)}
          aria-expanded={howToDisableOpen}
          aria-controls="how-to-disable-panel"
          id="how-to-disable-heading"
          data-testid="how-to-disable-toggle"
        >
          {howToDisableOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          How to disable context sharing
        </button>

        {howToDisableOpen && (
          <div
            id="how-to-disable-panel"
            className="mt-3 rounded-md border bg-card p-4 space-y-3 text-sm"
            data-testid="how-to-disable-panel"
          >
            <p>
              Enable <strong>Quiet mode</strong> to stop sending live context to the AI. The assistant will still work
              but will have no awareness of which record you are viewing or who you are.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={quietMode}
                onClick={() => setQuietMode(!quietMode)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  quietMode ? "bg-primary" : "bg-input"
                }`}
                data-testid="quiet-mode-toggle"
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform ${
                    quietMode ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
              <span className="font-medium">{quietMode ? "Quiet mode on — context not sent" : "Quiet mode off"}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              When quiet mode is on the daily cost cap also drops to $0, preventing any AI usage until you turn it off.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
